// The Miku Quest (Luxor's meme, 2026-10-02): the Everything Quest in reverse. Lose with every
// class, the 20 human jobs and the 55 monsters, once per round, which makes four quests.
// tools/buildLoserQuest.mjs builds loserQuest.json; this names the quest and its rounds and
// reads a player's row. It shipped as the Loser Quest and was renamed the same day for
// MikuShikhu, who volunteered. Internally it's still 'loser': the quest id, ?quest=loser, the
// data file and the ticker's loser1–loser4, so old links and published data keep working.

// The icon is Clockwork Coliseum's dove class icon, copied into the repo.
export const MIKU_QUEST = {
  name: 'Miku Quest',
  emoji: '🕊️',   // where only text fits: the coins, the ticker, What's New
  icon: new URL('../../assets/images/miku-dove.png', import.meta.url).href,
};

export const LOSER_ROUNDS = [
  { round: 1, id: 'loser1', name: 'First-Round Exit', emoji: '🚪', desc: 'lose a quarterfinal with every class' },
  { round: 2, id: 'loser2', name: 'Semifinal Heartbreak', emoji: '💔', desc: 'lose a semifinal with every class' },
  { round: 3, id: 'loser3', name: 'So Close', emoji: '🥈', desc: 'lose the final with every class' },
  { round: 4, id: 'loser4', name: 'Always the Bridesmaid', emoji: '💐', desc: 'win the final and then lose the title match, with every class' },
];

// The classes the file counts, in its index order. The first build (monsters only) called
// the list `monsters`; a page can meet that file until the box rebuilds it.
export function loserClasses(file) {
  return (file && (file.classes || file.monsters)) || [];
}

// One player's row for one round → { laps: [ms each full set was completed], counts:
// { class: times lost with it in that round } }. In the file a row is
// [display, r1, r2, r3, r4], and a round is 0 or [[laps…], classIdx, count, …].
export function loserRound(file, row, round) {
  const out = { laps: [], counts: {} };
  const cell = row && row[round];
  if (!cell) return out;
  const names = loserClasses(file);
  out.laps = cell[0] || [];
  for (let i = 1; i + 1 < cell.length; i += 2) out.counts[names[cell[i]]] = cell[i + 1];
  return out;
}

// A player's row by name: the file is keyed like the other quest files (lowercase, runs of
// spaces/underscores folded to one underscore).
export function loserRow(file, name) {
  const key = String(name || '').toLowerCase().replace(/[\s_]+/g, '_').replace(/^_+|_+$/g, '');
  return (key && file && file.players && file.players[key]) || null;
}

// The Full Miku (2026-10-20): all four rounds at once, every class lost with in each, so
// 4 × 75 targets. Its first tab on the Quests page. A round's full sets are counted by its
// least-lost class, so the Full Miku count is the fewest of any round's, and the Nth Full
// Miku is dated when the last round reached its Nth set. Exact, from the round dates the
// build already keeps: no new data.
export const FULL_MIKU = { round: 0, id: 'loserFull', name: 'Full Miku', emoji: '🕊️', desc: 'lose with every class in all four rounds' };
export const LOSER_TABS = [FULL_MIKU, ...LOSER_ROUNDS];

// One player's row → { laps: [ms each Full Miku was completed], rounds: [counts r1…r4],
// have: class×round cells with at least one loss, total: 4 × classes }.
export function fullMiku(file, row) {
  const rs = LOSER_ROUNDS.map((R) => loserRound(file, row, R.round));
  const sets = Math.min(...rs.map((r) => r.laps.length));
  const laps = [];
  for (let i = 0; i < sets; i++) laps.push(Math.max(...rs.map((r) => r.laps[i])));
  const names = loserClasses(file);
  const have = rs.reduce((a, r) => a + names.filter((c) => r.counts[c]).length, 0);
  return { laps, rounds: rs.map((r) => r.counts), have, total: names.length * LOSER_ROUNDS.length };
}
